"use client";

import { useEffect } from "react";

interface NavigationPerformanceEntry extends PerformanceNavigationTiming {
    deliveryType?: string;
}

function reportPerformance(): void {
    const navigation = performance.getEntriesByType("navigation")[0] as
        | NavigationPerformanceEntry
        | undefined;
    if (!navigation) return;

    const jsResources = performance
        .getEntriesByType("resource")
        .filter((entry): entry is PerformanceResourceTiming => {
            return entry.name.endsWith(".js") || entry.name.includes(".js?");
        });

    const payload = {
        message: "Frontend performance sample",
        level: "INFO",
        type: "web-performance",
        route: window.location.pathname,
        build_id: process.env.NEXT_PUBLIC_APP_BUILD_ID ?? null,
        navigation_type: navigation.type,
        delivery_type: navigation.deliveryType ?? null,
        ttfb_ms: Math.round(navigation.responseStart - navigation.requestStart),
        dom_content_loaded_ms: Math.round(navigation.domContentLoadedEventEnd),
        load_event_ms: Math.round(navigation.loadEventEnd),
        js_transfer_bytes: jsResources.reduce((total, entry) => total + entry.transferSize, 0),
        js_encoded_bytes: jsResources.reduce((total, entry) => total + entry.encodedBodySize, 0),
        js_resource_count: jsResources.length,
        connection_type:
            "connection" in navigator
                ? (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType ?? null
                : null,
    };

    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon("/api/error-report", blob)) return;
    }

    void fetch("/api/error-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
    });
}

export function WebPerformanceReporter() {
    useEffect(() => {
        let timer = 0;
        const scheduleReport = () => {
            timer = window.setTimeout(reportPerformance, 0);
        };

        if (document.readyState === "complete") {
            scheduleReport();
        } else {
            window.addEventListener("load", scheduleReport, { once: true });
        }

        return () => {
            window.clearTimeout(timer);
            window.removeEventListener("load", scheduleReport);
        };
    }, []);

    return null;
}
